import React from "react";
import { useIntl } from "react-intl";

import { DateInput, DateTimeInput } from "src/common/components/DateTime";
import { Input } from "src/common/components/Input";
import { NumberInput } from "src/common/components/NumberInput";
import { PasswordInput } from "src/common/components/PasswordInput";
import { PhoneNumberInput } from "src/common/components/PhoneNumberInput";
import { SocialSecurityNumberInput } from "src/common/components/SocialSecurityNumberInput";
import { IsLockedContext } from "src/features/forms/context/IsLockedContext";
import {
	useLogProp,
	useMessageProps,
	useValue
} from "src/features/forms/hooks/useField";
import { InputProps } from "src/features/forms/types";

export const InputRenderer: React.FunctionComponent<
	InputProps
> = directProps => {
	if (directProps.type === "number") {
		return <NumberInputRenderer {...directProps} />;
	}
	if (directProps.type === "password") {
		return <PasswordInputRenderer {...directProps} />;
	}
	if (directProps.type === "phone-number") {
		return <PhoneNumberInputRenderer {...directProps} />;
	}
	if (directProps.type === "date") {
		return <DateInputRenderer {...directProps} />;
	}
	if (directProps.type === "date-time") {
		return <DateTimeInputRenderer {...directProps} />;
	}
	if (directProps.type === "social-security-number") {
		return <SocialSecurityNumberInputRenderer {...directProps} />;
	}
	return <SimpleInputRenderer {...directProps} />;
};

const NumberInputRenderer: React.FunctionComponent<InputProps> = ({
	placeholder,
	disabled,
	...directProps
}) => {
	const { formatMessage } = useIntl();
	const { isLocked, ...inputProps } = useNumberInputProps();
	return (
		<NumberInput
			placeholder={
				placeholder === undefined
					? undefined
					: formatMessage(placeholder)
			}
			disabled={disabled || isLocked}
			{...directProps}
			{...inputProps}
		/>
	);
};

const PasswordInputRenderer: React.FunctionComponent<InputProps> = ({
	placeholder,
	disabled,
	...directProps
}) => {
	const { formatMessage } = useIntl();
	const { isLocked, ...inputProps } = useInputProps();
	return (
		<PasswordInput
			placeholder={
				placeholder === undefined
					? undefined
					: formatMessage(placeholder)
			}
			disabled={disabled || isLocked}
			{...directProps}
			{...inputProps}
		/>
	);
};

const PhoneNumberInputRenderer: React.FunctionComponent<InputProps> = ({
	placeholder,
	disabled,
	...directProps
}) => {
	const { formatMessage } = useIntl();
	const { isLocked, ...inputProps } = useInputProps();
	return (
		<PhoneNumberInput
			placeholder={
				placeholder === undefined
					? undefined
					: formatMessage(placeholder)
			}
			disabled={disabled || isLocked}
			{...directProps}
			{...inputProps}
		/>
	);
};

const DateInputRenderer: React.FunctionComponent<InputProps> = ({
	placeholder,
	disabled,
	...directProps
}) => {
	const { formatMessage } = useIntl();
	const { isLocked, ...inputProps } = useInputProps();
	return (
		<DateInput
			placeholder={
				placeholder === undefined
					? undefined
					: formatMessage(placeholder)
			}
			disabled={disabled || isLocked}
			{...directProps}
			{...inputProps}
		/>
	);
};

const DateTimeInputRenderer: React.FunctionComponent<InputProps> = ({
	placeholder,
	disabled,
	...directProps
}) => {
	const { isLocked, ...inputProps } = useInputProps();
	return (
		<DateTimeInput
			disabled={disabled || isLocked}
			{...directProps}
			{...inputProps}
		/>
	);
};

const SocialSecurityNumberInputRenderer: React.FunctionComponent<
	InputProps
> = ({ placeholder, disabled, ...directProps }) => {
	const { formatMessage } = useIntl();
	const { isLocked, ...inputProps } = useInputProps();
	return (
		<SocialSecurityNumberInput
			placeholder={
				placeholder === undefined
					? undefined
					: formatMessage(placeholder)
			}
			disabled={disabled || isLocked}
			{...directProps}
			{...inputProps}
		/>
	);
};

const SimpleInputRenderer: React.FunctionComponent<InputProps> = ({
	placeholder,
	disabled,
	...directProps
}) => {
	const { formatMessage } = useIntl();
	const { isLocked, ...inputProps } = useInputProps();
	return (
		<Input
			placeholder={
				placeholder === undefined
					? undefined
					: formatMessage(placeholder)
			}
			disabled={disabled || isLocked}
			{...directProps}
			{...inputProps}
		/>
	);
};

function useInputProps(): {
	isLocked: boolean | undefined;
	decorator: string | undefined;
	value: string | undefined;
	onChangeValue(value: string | undefined): void;
} {
	const isLocked = React.useContext(IsLockedContext);
	const decorator = useUnit();
	const [value, onChangeValue] = useValue<string | undefined>(undefined);
	return { isLocked, decorator, value, onChangeValue };
}

function useNumberInputProps(): {
	isLocked: boolean | undefined;
	decorator: string | undefined;
	value: number | undefined;
	onChangeValue(value: number | undefined): void;
} {
	const isLocked = React.useContext(IsLockedContext);
	const decorator = useUnit();
	const [value, onChangeValue] = useValue<number | undefined>(undefined);
	return { isLocked, decorator, value, onChangeValue };
}

function useUnit(): string | undefined {
	const messageProps = useMessageProps();
	return useLogProp<string | undefined>(messageProps, "unit", undefined);
}
